import { Component, DestroyRef, inject, Input, OnChanges, OnInit } from '@angular/core';
import { ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { combineLatest } from 'rxjs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SearchInputComponent } from '../../inputs/search-input/search-input.component';
import {
  SortDropdownComponent,
  SortDropdownGroup,
  buildSortGroups,
} from '../../inputs/sort-dropdown/sort-dropdown.component';
import { PaginationComponent } from '../../inputs/pagination/pagination.component';
import { ContactDetailComponent } from '../../organization/contacts/contact-detail/contact-detail.component';
import { PromptCardComponent } from '../../toasts-and-cards/prompt-card/prompt-card.component';
import {
  IContact,
  IValidatedContactBatchLine,
} from '../../../types/contactModule';
import { EditContactModalComponent } from '../../forms/edit-contact-modal/edit-contact-modal.component';
import { CsvServiceService } from '../../../services/csv/csv-service.service';
import { Router } from '@angular/router';
import { AssignMultipleContactsToOrganizationsComponent } from '../assign-multiple-contacts-to-organizations/assign-multiple-contacts-to-organizations.component';
import { AssignMultipleContactsToTagsComponent } from '../assign-multiple-contacts-to-tags/assign-multiple-contacts-to-tags.component';
import { ContactsService } from '../../../services/contacts/contacts.service';
import { DataTableComponent } from '../../ui/data-table/data-table.component';
import { StatusComponent } from '../../ui/status/status.component';
import { EmptyStateComponent } from '../../ui/empty-state/empty-state.component';
import { HasPermissionDirective } from '../../../directives/has-permission.directive';
import { PermissionsService } from '../../../services/permissions/permissions.service';
import { issueText, rowIssues } from '../csv-cleanup-table/csv-cleanup-table.component';

@Component({
  selector: 'csv-feedback-contacts-table',
  standalone: true,
  imports: [
    SearchInputComponent,
    MatTooltipModule,
    SortDropdownComponent,
    PaginationComponent,
    StatusComponent,
    EmptyStateComponent,
    HasPermissionDirective,
    ContactDetailComponent,
    PromptCardComponent,
    EditContactModalComponent,
    AssignMultipleContactsToOrganizationsComponent,
    AssignMultipleContactsToTagsComponent,
    DataTableComponent,
  ],
  templateUrl: './csv-feedback-contacts-table.component.html',
  styleUrls: [
    '../csv-cleanup-table/csv-import-table.css',
    './csv-feedback-contacts-table.component.css',
  ],
})
export class CsvFeedbackContactsTableComponent implements OnInit, OnChanges {
  @ViewChild(SearchInputComponent) searchInput?: SearchInputComponent;

  @Input() currentView: string = 'success';

  router = inject(Router);
  csvService = inject(CsvServiceService);
  contactService = inject(ContactsService);
  private permissions = inject(PermissionsService);
  private destroyRef = inject(DestroyRef);

  readonly issueText = issueText;

  /** Bulk actions offered for created contacts, from permissions. */
  can = { manageGroups: false, delete: false };

  get isFailedView(): boolean {
    return this.currentView === 'fail';
  }

  // Search functionality
  searchTerm: string = '';

  // Sort functionality
  sortCriteria: string = 'fullName,asc';

  readonly sortGroups: SortDropdownGroup[] = buildSortGroups({
    nameField: 'fullName',
    createdField: 'createdAt',
    modifiedField: 'modifiedAt',
  });


  handleAction(action: string) {
    // Failed rows were never saved: they have no uuid to act on.
    if (this.selectedContacts.length === 0 || this.isFailedView) return;
    switch (action) {
      case 'send-sms':
        this.sendSMStoSelectedContacts();
        break;
      case 'move-to-organization':
        this.openAssignMultipleContactsToOrg();
        break;
      case 'add-tags':
        this.openAssignMultipleContactsToTags();
        break;
      case 'delete':
        this.openDeleteSelectedContactsPrompt();
        break;
    }
  }

  // Contact selection
  selectedContacts: IValidatedContactBatchLine[] = [];

  // Actions modals
  showAssignMultipleContactsToOrg: boolean = false;
  showAssignMultipleContactsToTags: boolean = false;
  showDeleteSelectedContactsPrompt: boolean = false;

  // Search functionality
  updateSearchTerm(term: string) {
    this.searchTerm = term;
    this.currentPage = 0;
  }

  // Sort functionality
  updateSortCriteria(criteria: string) {
    this.sortCriteria = criteria;
    this.currentPage = 0;
  }

  // Contact selection methods
  selectContact(contact: IValidatedContactBatchLine) {
    const index = this.selectedContacts.findIndex(
      (c) => c.personEntity.id === contact.personEntity.id
    );
    if (index > -1) {
      this.selectedContacts.splice(index, 1);
    } else {
      this.selectedContacts.push(contact);
    }
  }

  selectAllContacts() {
    this.selectedContacts = [...this.getFilteredContacts];
  }

  deselectAllContacts() {
    this.selectedContacts = [];
  }

  toggleSelectAllContacts() {
    if (this.selectedContacts.length === this.getFilteredContacts.length) {
      this.deselectAllContacts();
    } else {
      this.selectAllContacts();
    }
  }

  isContactSelected(contact: IValidatedContactBatchLine): boolean {
    return this.selectedContacts.some(
      (c) => c.personEntity.id === contact.personEntity.id
    );
  }

  get isAllSelected(): boolean {
    return (
      this.selectedContacts.length === this.getFilteredContacts.length &&
      this.getFilteredContacts.length > 0
    );
  }

  // Actions methods
  openAssignMultipleContactsToOrg() {
    if (this.selectedContacts.length > 0) {
      this.showAssignMultipleContactsToOrg = true;
    }
  }

  closeAssignMultipleContactsToOrg() {
    this.showAssignMultipleContactsToOrg = false;
  }

  openAssignMultipleContactsToTags() {
    if (this.selectedContacts.length > 0) {
      this.showAssignMultipleContactsToTags = true;
    }
  }

  closeAssignMultipleContactsToTags() {
    this.showAssignMultipleContactsToTags = false;
  }

  openDeleteSelectedContactsPrompt() {
    if (this.selectedContacts.length > 0) {
      this.showDeleteSelectedContactsPrompt = true;
    }
  }

  closeDeleteSelectedContactsPrompt() {
    this.showDeleteSelectedContactsPrompt = false;
  }

  sendSMStoSelectedContacts() {
    if (this.selectedContacts.length > 0) {
      const params = {
        items: JSON.stringify(this.selectedContacts.map((c) => c.personEntity)),
      };
      this.router.navigate(['/organization/messages/create'], {
        queryParams: params,
      });
      this.resetSelectedContacts();
    }
  }

  deleteSelectedContacts() {
    if (this.selectedContacts.length > 0) {
      // Get contact UUIDs for backend deletion
      const contactUuids = this.selectedContacts.map(
        (c) => c.personEntity.uuid as string
      );

      // Delete from backend first
      this.contactService.deleteSelectedContacts(contactUuids).subscribe({
        next: () => {
          // After successful backend deletion, update local storage
          const contactIds = this.selectedContacts.map(
            (c) => c.personEntity.id
          );

          if (this.currentView === 'success') {
            // Remove from successful contacts
            const successfulContacts =
              this.csvService.getSuccessfulContacts.filter(
                (c: IValidatedContactBatchLine) =>
                  !contactIds.includes(c.personEntity.id)
              );
            this.csvService.updateSuccessfulContacts(successfulContacts);
          } else {
            // Remove from failed contacts
            const failedContacts = this.csvService.getFailedContacts.filter(
              (c: IValidatedContactBatchLine) =>
                !contactIds.includes(c.personEntity.id)
            );
            this.csvService.updateFailedContacts(failedContacts);
          }

          // Update the local contacts array to reflect changes
          this.loadView();

          this.resetSelectedContacts();
          this.closeDeleteSelectedContactsPrompt();
            },
        error: (err) => {
          // Even if backend deletion fails, we can still remove from local storage
          // or show an error message to the user
        },
      });
    }
  }

  resetSelectedContacts() {
    this.selectedContacts = [];
  }

  // Getter methods for modal data
  get selectedContactsForModals(): IContact[] {
    return this.selectedContacts.map((c) => c.personEntity);
  }

  // Pagination — render only the current page's rows. Rendering every row at once
  // (each with a dropdown + icons) is what froze the UI on large result sets.
  pageSize = 50;
  currentPage = 0;

  // Cache the filtered+sorted result. Without it, the getter re-sorts the whole
  // array on every change-detection read (and pagination reads it several times
  // per cycle).
  private _cachedFiltered: IValidatedContactBatchLine[] = [];
  private _lastContactsRef: IValidatedContactBatchLine[] | null = null;
  private _lastSearch = '\u0000';
  private _lastSort = '\u0000';

  private _invalidateFilterCache() {
    this._lastContactsRef = null;
  }

  // Get filtered contacts with search (cached)
  get getFilteredContacts(): IValidatedContactBatchLine[] {
    if (
      this._lastContactsRef === this.contacts &&
      this._lastSearch === this.searchTerm &&
      this._lastSort === this.sortCriteria
    ) {
      return this._cachedFiltered;
    }
    this._cachedFiltered = this._computeFilteredContacts();
    this._lastContactsRef = this.contacts;
    this._lastSearch = this.searchTerm;
    this._lastSort = this.sortCriteria;
    return this._cachedFiltered;
  }

  get totalPages(): number {
    return Math.max(
      1,
      Math.ceil(this.getFilteredContacts.length / this.pageSize)
    );
  }

  get pagedContacts(): IValidatedContactBatchLine[] {
    const list = this.getFilteredContacts;
    const lastPage = Math.max(0, Math.ceil(list.length / this.pageSize) - 1);
    const page = Math.min(Math.max(0, this.currentPage), lastPage);
    const start = page * this.pageSize;
    return list.slice(start, start + this.pageSize);
  }

  goToPage(page: number) {
    this.currentPage = page;
  }

  nextPage() {
    this.currentPage += 1;
  }

  prevPage() {
    if (this.currentPage > 0) this.currentPage -= 1;
  }

  changePageSize(size: number) {
    this.pageSize = size;
    this.currentPage = 0;
  }

  private _computeFilteredContacts() {
    // A copy: sorting must never reorder the service's own list.
    let filteredContacts = [...this.contacts];

    // Apply search filter if search term exists
    if (this.searchTerm.trim()) {
      const searchLower = this.searchTerm.toLowerCase().trim();
      filteredContacts = filteredContacts.filter((contact) => {
        const firstName = contact.personEntity.firstName?.toLowerCase() || '';
        const lastName = contact.personEntity.lastName?.toLowerCase() || '';
        const fullName = `${firstName} ${lastName}`.trim();
        const email = contact.personEntity.emailAddress?.toLowerCase() || '';
        const phone =
          contact.personEntity.primaryContactNumber?.toLowerCase() || '';
        const gender = contact.personEntity.gender?.toLowerCase() || '';

        return (
          fullName.includes(searchLower) ||
          email.includes(searchLower) ||
          phone.includes(searchLower) ||
          gender.includes(searchLower) ||
          firstName.includes(searchLower) ||
          lastName.includes(searchLower)
        );
      });
    }

    // Apply sorting
    if (this.sortCriteria) {
      const [field, direction] = this.sortCriteria.split(',');
      const isAscending = direction === 'asc';

      filteredContacts.sort((a, b) => {
        let aValue: any;
        let bValue: any;

        switch (field) {
          case 'fullName':
            aValue = `${a.personEntity.firstName || ''} ${
              a.personEntity.lastName || ''
            }`.trim();
            bValue = `${b.personEntity.firstName || ''} ${
              b.personEntity.lastName || ''
            }`.trim();
            break;
          case 'createdAt':
            aValue = a.personEntity.createdAt || '';
            bValue = b.personEntity.createdAt || '';
            break;
          case 'modifiedAt':
            aValue = a.personEntity.modifiedAt || '';
            bValue = b.personEntity.modifiedAt || '';
            break;
          default:
            aValue = a.personEntity.firstName || '';
            bValue = b.personEntity.firstName || '';
        }

        // Handle null/undefined values
        if (!aValue && !bValue) return 0;
        if (!aValue) return isAscending ? 1 : -1;
        if (!bValue) return isAscending ? -1 : 1;

        // String comparison
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          const comparison = aValue
            .toLowerCase()
            .localeCompare(bValue.toLowerCase());
          return isAscending ? comparison : -comparison;
        }

        // Date comparison
        if (aValue instanceof Date && bValue instanceof Date) {
          const comparison = aValue.getTime() - bValue.getTime();
          return isAscending ? comparison : -comparison;
        }

        // Default comparison
        if (aValue < bValue) return isAscending ? -1 : 1;
        if (aValue > bValue) return isAscending ? 1 : -1;
        return 0;
      });
    }

    return filteredContacts;
  }

  // Edit Contact stuff
  openEditContactModal: boolean = false;
  selectedContact: IContact | null = null;
  showContactDetail: boolean = false;
  showDeletePrompt: boolean = false;

  contacts: IValidatedContactBatchLine[] = [];

  updateSelectedContact(contact: IContact) {
    this.selectedContact = contact;
  }
  openContactDetail(contact: IContact) {
    this.updateSelectedContact(contact);
    this.showContactDetail = true;
  }
  closeContactDetail() {
    this.showContactDetail = false;
  }
  openEditContact(contact: IContact) {
    this.updateSelectedContact(contact);
    this.openEditContactModal = true;
  }
  closeEditContact() {
    this.openEditContactModal = false;
  }

  openDeletePrompt(contact: IContact) {
    this.updateSelectedContact(contact);
    this.showDeletePrompt = true;
  }
  closeDeletePrompt() {
    this.showDeletePrompt = false;
  }

  // delete the contact from the upload contact list
  async deleteContact() {}

  rowKey(line: IValidatedContactBatchLine, index: number): string {
    return String(line.personEntity?.uuid ?? line.personEntity?.id ?? `i-${index}`);
  }

  name(line: IValidatedContactBatchLine | null): string {
    const p = line?.personEntity;
    return `${p?.firstName ?? ''} ${p?.lastName ?? ''}`.trim() || 'No name';
  }

  issues(line: IValidatedContactBatchLine) {
    return rowIssues(line);
  }

  groupNames(items: { name?: string | null }[] | undefined): string {
    const names = (items ?? []).map((item) => item?.name).filter(Boolean) as string[];
    if (!names.length) return '—';
    return names.length === 1 ? names[0] : `${names[0]} +${names.length - 1}`;
  }

  get selectionLabel(): string {
    const count = this.selectedContacts.length;
    return count === 1 ? '1 contact selected' : `${count} contacts selected`;
  }

  get someSelected(): boolean {
    return this.selectedContacts.length > 0 && !this.isAllSelected;
  }

  clearSearch() {
    this.searchInput?.writeValue('');
    this.searchTerm = '';
    this.currentPage = 0;
  }

  /** Row click opens the contact panel; Enter does the same from the keyboard. */
  openFromKeyboard(event: Event, line: IValidatedContactBatchLine) {
    if (event.target !== event.currentTarget) return;
    event.preventDefault();
    this.openContactDetail(line.personEntity);
  }

  ngOnInit() {
    combineLatest([
      this.permissions.hasPermission('CONTACT_MANAGEMENT', 'manage-contact-groups'),
      this.permissions.hasPermission('CONTACT_MANAGEMENT', 'delete-contacts'),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([manageGroups, canDelete]) => {
        this.can = { manageGroups: !!manageGroups, delete: !!canDelete };
      });
  }

  ngOnChanges() {
    this.resetSelectedContacts();
    this.loadView();
  }

  private loadView() {
    if (this.currentView == 'success') {
      this.contacts = this.csvService.getSuccessfulContacts;
    } else {
      this.contacts = this.csvService.getFailedContacts;
    }
    // New contacts reference invalidates the filter cache automatically; reset to
    // the first page so we never land on a now-out-of-range page.
    this._invalidateFilterCache();
    this.currentPage = 0;
  }
}
